import { useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Dialog } from './dialog';
import { FileViewer } from './file-viewer';

export type ViewableFile = {
  url: string;
  file_name: string;
  mime_type: string;
};

/**
 * Modal previewer for an attachment — images and PDFs render inline via
 * FileViewer, so a receipt can be checked without leaving the page. Pass
 * `file = null` to keep it closed. Provide `onPrev`/`onNext` to turn it into a
 * lightbox that steps through a list; omit them (or pass undefined at an edge)
 * to hide that arrow.
 */
export function AttachmentViewer({
  file,
  onClose,
  onPrev,
  onNext,
  position,
}: {
  file: ViewableFile | null;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  position?: string;
}) {
  useEffect(() => {
    if (!file) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'ArrowLeft' && onPrev) onPrev();
      if (e.key === 'ArrowRight' && onNext) onNext();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [file, onPrev, onNext]);

  const showNav = onPrev !== undefined || onNext !== undefined;

  return (
    <Dialog open={file != null} onClose={onClose} title={file?.file_name ?? 'File'} size="xl">
      {file ? (
        <div className="relative h-[75vh] w-full overflow-auto rounded border border-[var(--color-border)] bg-[var(--color-surface-muted)]">
          <FileViewer url={file.url} mimeType={file.mime_type} fileName={file.file_name} />

          {showNav ? (
            <>
              <NavButton side="left" onClick={onPrev} />
              <NavButton side="right" onClick={onNext} />
              {position ? (
                <span className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-2.5 py-1 text-xs font-medium text-white">
                  {position}
                </span>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
    </Dialog>
  );
}

function NavButton({ side, onClick }: { side: 'left' | 'right'; onClick?: () => void }) {
  const Icon = side === 'left' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      aria-label={side === 'left' ? 'Previous file' : 'Next file'}
      className={`absolute top-1/2 z-10 flex size-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white shadow-lg transition hover:bg-black/80 disabled:pointer-events-none disabled:opacity-0 ${
        side === 'left' ? 'left-3' : 'right-3'
      }`}
    >
      <Icon className="size-5" />
    </button>
  );
}
